/**
 * Left Sidebar Component
 *
 * Purpose: Reusable vertical navigation component with icon-based menu for quick access
 *          to main app sections with tooltips, active states, and collapsible behavior
 *
 * Architecture: Material-UI Drawer with configurable navigation items and responsive design
 *              Uses Orchestrate brand colors (Deep Teal #0A413C) for dark theme
 *
 * Dependencies: React, Material-UI, React Router
 * Author: Development Team
 * Created: 2025-08-07
 * Updated: 2025-11-29 - Implemented dark theme with brand colors per Figma design
 */

import React from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Box,
  Divider,
  Typography,
  Popover,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Dashboard, EventNote, ChevronLeft, ChevronRight, Analytics } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Custom icon component for SVG icons from public folder
interface CustomIconProps {
  src: string;
  alt: string;
  size?: number;
}

const CustomIcon: React.FC<CustomIconProps> = ({ src, alt, size = 24 }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      width: size,
      height: size,
      objectFit: 'contain',
    }}
  />
);
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNavigationGuard } from '../../context/NavigationGuardContext';
import { ModuleKey } from '../../constants/permissionKeys';
import { brandColors } from '../../theme/orchestrateTheme';
import BuildInfo from './BuildInfo';

// Sidebar width constants - expanded per brand spec
const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 72;

// Home route — shared by the Home nav item and the logo link so they can't drift (ORCH-431)
export const HOME_PATH = '/home';

// Sidebar dark purple color - darker than deepViolet per Figma design
const SIDEBAR_DARK_PURPLE = '#2D0A35';

// Styled components with dark purple theme (per Figma design)
const StyledSidebar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ open }) => ({
  width: open ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  backgroundColor: SIDEBAR_DARK_PURPLE,
  borderRight: 'none',
  transition: 'width 0.2s ease-in-out',
  overflowX: 'hidden',
  height: '100vh',
  position: 'sticky',
  top: 0,
  display: 'flex',
  flexDirection: 'column',
}));

const NavigationList = styled(List)({
  padding: '8px 0',
  flex: 1,
});

const BottomList = styled(List)({
  marginTop: 'auto',
  padding: '8px 0',
  flexShrink: 0,
});

// Navigation item interface
interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  requiresAuth?: boolean;
  /** Area the item opens; shown only when the user holds it (ORCH-334). Unset = every member */
  module?: ModuleKey;
  // When true the item is visible but not yet implemented — clicking shows a
  // "coming soon" notification instead of navigating to a dead route.
  comingSoon?: boolean;
}

// Top section navigation items - matches Figma design order
const TOP_NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: <CustomIcon src="/icons/orc_icon_home.svg" alt="Home" size={32} />,
    path: HOME_PATH,
    requiresAuth: true,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
    requiresAuth: true,
  },
  {
    id: 'planning',
    label: 'Planning',
    icon: <EventNote />,
    path: '/planning',
    requiresAuth: true,
    module: ModuleKey.PLANNING,
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <Analytics />,
    path: '/analytics',
    requiresAuth: true,
    module: ModuleKey.ANALYTICS,
    comingSoon: true,
  },
];

// Bottom section navigation items
const BOTTOM_NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: 'admin',
    label: 'Admin',
    icon: <CustomIcon src="/icons/orc_icon_settings.svg" alt="Admin" />,
    path: '/admin',
    requiresAuth: true,
    module: ModuleKey.ADMIN,
  },
];

interface LeftSidebarProps {
  open: boolean;
  onToggle: () => void;
}

/**
 * Left Sidebar Component
 *
 * Purpose: Provides icon-based navigation to core app features with collapse/expand functionality
 * Logic: Shows navigation items based on user permissions and highlights current page
 * Input: open - Whether sidebar is expanded
 *        onToggle - Function to toggle sidebar state
 * Output: JSX element for left sidebar navigation
 *
 * @param open - Whether sidebar is expanded
 * @param onToggle - Function to toggle sidebar state
 * @returns JSX element for left sidebar navigation
 */
const LeftSidebar: React.FC<LeftSidebarProps> = ({ open, onToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, hasModule } = useAuth();
  const { confirmNavigation } = useNavigationGuard();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [comingSoonMessage, setComingSoonMessage] = React.useState<string | null>(null);
  const [comingSoonAnchor, setComingSoonAnchor] = React.useState<HTMLElement | null>(null);
  const comingSoonTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeComingSoon = React.useCallback(() => {
    if (comingSoonTimerRef.current !== null) {
      clearTimeout(comingSoonTimerRef.current);
      comingSoonTimerRef.current = null;
    }
    setComingSoonAnchor(null);
    setComingSoonMessage(null);
  }, []);

  React.useEffect(() => {
    return () => {
      if (comingSoonTimerRef.current !== null) {
        clearTimeout(comingSoonTimerRef.current);
      }
    };
  }, []);

  // Debug logging removed for production

  /**
   * Checks if navigation item should be displayed based on user permissions
   *
   * @param item - Navigation item to check
   * @returns Boolean indicating if item should be shown
   */
  const shouldShowItem = (item: NavigationItem): boolean => {
    return !item.module || hasModule(item.module);
  };

  /**
   * Checks if navigation item is currently active
   *
   * @param path - Navigation item path
   * @returns Boolean indicating if item is active
   */
  const isActive = (path: string): boolean => {
    return location.pathname === path;
  };

  /**
   * Handles navigation item click. If the item is flagged as coming-soon,
   * a popover is shown anchored to the clicked button so the message
   * appears next to the icon the user just clicked, rather than at the
   * bottom of the screen where it can be missed.
   *
   * @param item - Navigation item that was clicked
   * @param event - Click event used to anchor the coming-soon popover
   */
  const handleNavigation = (item: NavigationItem, event: React.MouseEvent<HTMLElement>) => {
    if (item.comingSoon) {
      setComingSoonMessage(`${item.label} is coming soon`);
      setComingSoonAnchor(event.currentTarget);
      if (comingSoonTimerRef.current !== null) {
        clearTimeout(comingSoonTimerRef.current);
      }
      comingSoonTimerRef.current = setTimeout(() => {
        closeComingSoon();
      }, 4000);
      return;
    }
    // A page with unsaved changes gets to ask first (ORCH-393)
    confirmNavigation(() => {
      navigate(item.path);
      // Close sidebar on mobile after navigation
      if (isMobile && open) {
        onToggle();
      }
    });
  };

  /**
   * Handles a click on the logo link. A plain left click routes in-app through the
   * unsaved-changes guard; modified or middle clicks fall through to the browser so
   * the link opens in a new tab.
   *
   * @param event - Click event on the logo link
   */
  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    confirmNavigation(() => {
      navigate(HOME_PATH);
      if (isMobile && open) {
        onToggle();
      }
    });
  };

  /**
   * Handles logout action
   */
  const handleLogout = () => {
    confirmNavigation(async () => {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Logout failed:', error);
      }
    });
  };

  /**
   * Renders a navigation item with dark theme styling
   *
   * @param item - Navigation item to render
   * @returns JSX element for navigation item
   */
  const renderNavigationItem = (item: NavigationItem) => {
    if (!shouldShowItem(item)) {
      return null;
    }

    const isItemActive = isActive(item.path);

    const listItemButton = (
      <ListItemButton
        data-testid={`nav-${item.id}`}
        onClick={(event) => handleNavigation(item, event)}
        sx={{
          minHeight: 48,
          justifyContent: 'center',
          px: open ? 2 : 0,
          mx: open ? 1 : 'auto',
          my: 0.5,
          borderRadius: 1,
          width: open ? 'auto' : 48,
          backgroundColor: isItemActive ? brandColors.purple : 'transparent',
          '&:hover': {
            backgroundColor: isItemActive ? brandColors.purple : 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 2 : 0,
            justifyContent: 'center',
            color: isItemActive ? brandColors.aqua : '#ffffff',
          }}
        >
          {item.icon}
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={item.label}
            sx={{
              opacity: 1,
              '& .MuiTypography-root': {
                color: isItemActive ? '#ffffff' : 'rgba(255, 255, 255, 0.85)',
                fontWeight: isItemActive ? 600 : 400,
                fontSize: '0.9rem',
              },
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
        {open ? (
          listItemButton
        ) : (
          <Tooltip title={item.label} placement="right" arrow>
            {listItemButton}
          </Tooltip>
        )}
      </ListItem>
    );
  };

  return (
    <StyledSidebar open={open}>
      {/* Sidebar Header with Logo - matches sidebar width exactly */}
      <Box
        sx={{
          width: '100%',
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-start' : 'center',
          px: open ? 2.5 : 0,
          py: 1.5,
          boxSizing: 'border-box',
        }}
      >
        {/* Logo links Home; a real link so middle/Ctrl/Cmd-click open a new tab (ORCH-431) */}
        <Tooltip title="Home" placement="right" arrow>
          <Box
            component={RouterLink}
            to={HOME_PATH}
            aria-label="Orchestrate home"
            data-testid="sidebar-logo-link"
            onClick={handleLogoClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'inherit',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            {/* Logo Image - blue spiral logo */}
            <Box
              component="img"
              src="/icons/orc_logo_icon_blue.svg"
              alt="Orchestrate"
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
              }}
            />
            {open && (
              <Typography
                variant="h6"
                sx={{
                  ml: 1.5,
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  letterSpacing: '-0.02em',
                }}
              >
                Orchestrate
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* Main Navigation - Top Section */}
      <NavigationList>{TOP_NAVIGATION_ITEMS.map(renderNavigationItem)}</NavigationList>

      {/* Divider for bottom section */}
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mx: 2 }} />

      {/* Bottom Navigation Section */}
      <BottomList>
        {/* Admin Navigation */}
        {BOTTOM_NAVIGATION_ITEMS.map(renderNavigationItem)}

        {/* Logout Item */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          {open ? (
            <ListItemButton
              onClick={handleLogout}
              data-testid="logout-button"
              sx={{
                minHeight: 48,
                justifyContent: 'initial',
                px: 2,
                mx: 1,
                my: 0.5,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: 2,
                  justifyContent: 'center',
                  color: '#ffffff',
                }}
              >
                <CustomIcon src="/icons/orc_icon_logout.svg" alt="Logout" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                sx={{
                  '& .MuiTypography-root': {
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: '0.9rem',
                  },
                }}
              />
            </ListItemButton>
          ) : (
            <Tooltip title="Logout" placement="right" arrow>
              <ListItemButton
                onClick={handleLogout}
                data-testid="logout-button"
                sx={{
                  minHeight: 48,
                  justifyContent: 'center',
                  px: 0,
                  mx: 'auto',
                  my: 0.5,
                  borderRadius: 1,
                  width: 48,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 0,
                    justifyContent: 'center',
                    color: '#ffffff',
                  }}
                >
                  <CustomIcon src="/icons/orc_icon_logout.svg" alt="Logout" />
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          )}
        </ListItem>
      </BottomList>

      {/* Collapse Toggle at Bottom */}
      <Box
        sx={{
          p: 1,
          display: 'flex',
          justifyContent: open ? 'flex-end' : 'center',
        }}
      >
        <IconButton
          onClick={onToggle}
          size="small"
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
            },
          }}
        >
          {open ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Box>

      {/* Build Info Footer */}
      <BuildInfo open={open} />

      {/* Coming-soon popover anchored to the clicked nav item so the message
          appears next to the icon rather than at the bottom of the screen. */}
      <Popover
        data-testid="coming-soon-popover"
        open={comingSoonAnchor !== null}
        anchorEl={comingSoonAnchor}
        onClose={closeComingSoon}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 6,
            sx: { ml: 1, overflow: 'visible' },
          },
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={closeComingSoon}
          sx={{ alignItems: 'center' }}
        >
          {comingSoonMessage ?? ''}
        </Alert>
      </Popover>
    </StyledSidebar>
  );
};

export default LeftSidebar;
